import { useEffect } from "react";
import GeorgiaHeroSection from "./components/HeroSection";
import GeorgiaCommunitiesWeServe from "./components/CommunitiesWeServe";
import GeorgiaCitiesDirectory from "./components/CitiesDirectory";
import GeorgiaStateResources from "./components/StateResources";
import GeorgiaFAQSection, { faqs } from "./components/FAQSection";
import GeorgiaFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function GeorgiaPage() {
  useEffect(() => {
    document.title = "Georgia Passport Services — Expedited & Emergency | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/georgia/";

    setLink("canonical", pageUrl);
    setMeta(
      "description",
      "Expedited passport services across Georgia — Atlanta, Savannah, Augusta, Macon, Athens. Emergency processing as fast as 24 hours. A+ BBB rated courier service."
    );
    setMeta("geo.placename", "Georgia, United States");
    setMeta("geo.region", "US-GA");
    setMeta("geo.position", "32.1656;-82.9001");
    setMeta("ICBM", "32.1656, -82.9001");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Georgia Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center serves Georgia travelers with expedited and emergency passport processing. Headquarters in Washington, DC with remote service across all Georgia cities.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        address: {
          "@type": "PostalAddress",
          streetAddress: "1629 K Street, Suite 300",
          addressLocality: "Washington",
          addressRegion: "DC",
          postalCode: "20006",
          addressCountry: "US",
        },
        areaServed: {
          "@type": "State",
          name: "Georgia",
          containedInPlace: {
            "@type": "Country",
            name: "United States",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "State",
          name: "Georgia",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <GeorgiaHeroSection />
      <GeorgiaCommunitiesWeServe />
      <GeorgiaCitiesDirectory />
      <GeorgiaStateResources />
      <GeorgiaFAQSection />
      <SEOCrossLinks state="GA" />
      <GeorgiaFinalCTA />
    </div>
  );
}